import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../../router/all_routes";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../../../core/data/redux/store";
import { TableData } from "../../../../core/data/interface";
import StudentModals from "../studentModals";
import Table from "../../../../core/common/dataTable/index";
import {
  deleteStudentApi,
  fetchStudentsApi,
} from "../../../../core/data/services/studentService";
import {
  fetchStudentsSuccess,
} from "../../../../core/data/redux/studentSlice";
import { Student } from "../../../../core/data/types/student";
import ErrorModal from "../../../../core/common/ErrorModal";
import SuccessModal from "../../../../core/common/successModal";

const StudentList = () => {
  const routes = all_routes;
 const hasFetched = useRef(false);
  const dispatch = useDispatch();

  // State to keep track of which student to delete
  const [deleteStudentId, setDeleteStudentId] = useState<number | null>(null);

  // States for success/error after deletion
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    const fetchData = async () => {
      try {
        const fetchedStudents = await fetchStudentsApi();
        console.log("Fetched Students:", fetchedStudents);
        dispatch(fetchStudentsSuccess(fetchedStudents));
      } catch (error: any) {
        console.error("Error fetching students:", error.message);
        // Optionally show an error modal here if initial fetch fails
        setErrorMessage("Failed to load students: " + error.message);
        setShowError(true);
      }
    };
    fetchData();
  }, [dispatch]);

  // Fetch students data from the Redux store
  const students = useSelector((state: RootState) => state.students.students);

  const tableData = students;
  console.log("Table Data:", tableData);


  // Handler to open delete modal and set student id
  const handleOpenDeleteModal = (id: number) => {
    setDeleteStudentId(id);
    // If your StudentModals (confirmation) needs any extra state, pass it in via props
    // The actual showing of the delete confirmation modal is handled by data-bs attributes in the Link
  };

  // Handler to confirm delete
  const handleConfirmDelete = async () => {
    if (deleteStudentId == null) return;
    try {
      await deleteStudentApi(deleteStudentId);
      // Refetch students after delete
      const updatedStudents = await fetchStudentsApi();
      dispatch(fetchStudentsSuccess(updatedStudents));

      // Show success message
      setSuccessMessage("Student deleted successfully.");
      setShowSuccess(true);
    } catch (error: any) {
      console.error("Error deleting student:", error.message);
      // Show error message
      setErrorMessage("Failed to delete student: " + error.message);
      setShowError(true);
    } finally {
      // Reset deleteStudentId so next time it's clean
      setDeleteStudentId(null);
    }
  };

  const columns = [
    {
      title: "Academic Year ID",
      dataIndex: "academicYearId",
      sorter: (a: TableData, b: TableData) =>
        (a.academicYearId as number) - (b.academicYearId as number),
    },
    {
      title: "First Name",
      dataIndex: "firstName",
      sorter: (a: TableData, b: TableData) =>
        a.firstName.localeCompare(b.firstName),
    },
    {
      title: "Last Name",
      dataIndex: "lastName",
      sorter: (a: TableData, b: TableData) =>
        a.lastName.localeCompare(b.lastName),
    },
    {
      title: "DOB",
      dataIndex: "dob",
      sorter: (a: TableData, b: TableData) =>
        a.dob.localeCompare(b.dob),
    },
    {
      title: "Blood Group",
      dataIndex: "bloodGroup",
      sorter: (a: TableData, b: TableData) =>
        a.bloodGroup.localeCompare(b.bloodGroup),
    },
    {
      title: "Email",
      dataIndex: "email",
      sorter: (a: TableData, b: TableData) =>
        a.email.localeCompare(b.email),
    },
    {
      title: "Admission Date",
      dataIndex: "admissionDate",
      sorter: (a: TableData, b: TableData) =>
        a.admissionDate.localeCompare(b.admissionDate),
    },
    {
      title: "Roll No",
      dataIndex: "rollNo",
      sorter: (a: TableData, b: TableData) =>
        a.rollNo.localeCompare(b.rollNo),
    },
    {
      title: "School Class ID",
      dataIndex: "schoolClassId",
      sorter: (a: TableData, b: TableData) =>
        (a.schoolClassId as number) - (b.schoolClassId as number),
    },
    {
      title: "Class Section ID",
      dataIndex: "classSectionId",
      sorter: (a: TableData, b: TableData) =>
        (a.classSectionId as number) - (b.classSectionId as number),
    },
    {
      title: "Gender",
      dataIndex: "gender",
      sorter: (a: TableData, b: TableData) =>
        a.gender.localeCompare(b.gender),
    },
    {
      title: "Religion",
      dataIndex: "religion",
      sorter: (a: TableData, b: TableData) =>
        a.religion.localeCompare(b.religion),
    },
    {
      title: "Caste",
      dataIndex: "caste",
      sorter: (a: TableData, b: TableData) =>
        a.caste.localeCompare(b.caste),
    },
    {
      title: "Caste Category",
      dataIndex: "casteCategory",
      sorter: (a: TableData, b: TableData) =>
        a.casteCategory.localeCompare(b.casteCategory),
    },
    {
      title: "Mother Tongue",
      dataIndex: "motherTongue",
      sorter: (a: TableData, b: TableData) =>
        a.motherTongue.localeCompare(b.motherTongue),
    },
    {
      title: "Aadhar Number",
      dataIndex: "aadharNumber",
      sorter: (a: TableData, b: TableData) =>
        a.aadharNumber.localeCompare(b.aadharNumber),
    },
    {
      title: "Aadhar Status",
      dataIndex: "aadharStatus",
      sorter: (a: TableData, b: TableData) =>
        a.aadharStatus.localeCompare(b.aadharStatus),
    },
    {
      title: "Physically Challenged",
      dataIndex: "physicallyChallenged",
      render: (val: boolean) => (val ? "Yes" : "No"),
      sorter: (a: TableData, b: TableData) =>
        Number(a.physicallyChallenged) - Number(b.physicallyChallenged),
    },
    {
      title: "MDM Enrolled",
      dataIndex: "mdmEnrolled",
      render: (val: boolean) => (val ? "Yes" : "No"),
      sorter: (a: TableData, b: TableData) =>
        Number(a.mdmEnrolled) - Number(b.mdmEnrolled),
    },
    {
      title: "Address",
      dataIndex: "address",
      sorter: (a: TableData, b: TableData) =>
        a.address.localeCompare(b.address),
    },
    {
      title: "Father Name",
      dataIndex: "fatherName",
      sorter: (a: TableData, b: TableData) =>
        a.fatherName.localeCompare(b.fatherName),
    },
    {
      title: "Mother Name",
      dataIndex: "motherName",
      sorter: (a: TableData, b: TableData) =>
        a.motherName.localeCompare(b.motherName),
    },
    {
      title: "Father Aadhar",
      dataIndex: "fatherAadhar",
      sorter: (a: TableData, b: TableData) =>
        a.fatherAadhar.localeCompare(b.fatherAadhar),
    },
    {
      title: "Mother Aadhar",
      dataIndex: "motherAadhar",
      sorter: (a: TableData, b: TableData) =>
        a.motherAadhar.localeCompare(b.motherAadhar),
    },
    {
      title: "Primary Contact No",
      dataIndex: "primaryContactNo",
      sorter: (a: TableData, b: TableData) =>
        a.primaryContactNo.localeCompare(b.primaryContactNo),
    },
    {
      title: "Secondary Contact No",
      dataIndex: "secondaryContactNo",
      sorter: (a: TableData, b: TableData) =>
        a.secondaryContactNo.localeCompare(b.secondaryContactNo),
    },
    {
      title: "School",
      dataIndex: "school",
      sorter: (a: TableData, b: TableData) =>
        a.school.localeCompare(b.school),
    },
    {
      title: "School Class",
      dataIndex: "schoolClass",
      sorter: (a: TableData, b: TableData) =>
        a.schoolClass.localeCompare(b.schoolClass),
    },
    {
      title: "Class Section",
      dataIndex: "classSection",
      sorter: (a: TableData, b: TableData) =>
        a.classSection.localeCompare(b.classSection),
    },
    {
      title: "Action",
      dataIndex: "action",
      render: (_: any, record: Student) => (
        <div className="d-flex align-items-center">
          <div className="dropdown">
            <Link
              to="#"
              className="btn btn-white btn-icon btn-sm d-flex align-items-center justify-content-center rounded-circle p-0"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <i className="ti ti-dots-vertical fs-14" />
            </Link>
            <ul className="dropdown-menu dropdown-menu-right p-3">
              <li>
                <Link className="dropdown-item rounded-1" to="student-details">
                  <i className="ti ti-menu me-2" />
                  View Student
                </Link>
              </li>
              <li>
                <Link
                  to={routes.editStudent.replace(":id", record.id?.toString())}
                  state={{ student: record }}
                  className="dropdown-item rounded-1"
                >
                  <i className="ti ti-edit-circle me-2" />
                  Edit
                </Link>
              </li>
              <li>
                <Link
                  className="dropdown-item rounded-1"
                  to="#"
                  data-bs-toggle="modal"
                  data-bs-target="#delete-modal"
                  onClick={() => handleOpenDeleteModal(record.id)}
                >
                  <i className="ti ti-trash-x me-2" />
                  Delete
                </Link>
              </li>
            </ul>
          </div>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="card">
            <div className="card-body p-0 py-3">
              {/* Pass students data to the Table */}
              <Table dataSource={tableData} columns={columns} Selection={true} />
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation modal for delete: StudentModals should render a bootstrap modal with id="delete-modal"
          and call onConfirmDelete when the user confirms. */}
      <StudentModals onConfirmDelete={handleConfirmDelete} />

      {/* Success Modal */}
      <SuccessModal
        show={showSuccess}
        message={successMessage}
        onClose={() => setShowSuccess(false)}
      />

      {/* Error Modal */}
      <ErrorModal
        show={showError}
        message={errorMessage}
        onClose={() => setShowError(false)}
      />
    </>
  );
};

export default StudentList;
